import { cleanComponentNameToValues } from "./cleanComponentNameToValues";
import { maximumComponentValuesForActive, minimumComponentValuesForActive } from "./constants";
import { extractLiquidMessageComponentNames } from "./extractLiquidMessageComponentNames";
import { mapEmailMissingActiveComponentNameToComposerPanel } from "./mapEmailMissingActiveComponentNameToComposerPanel";
import { resolveComponentName } from "./resolveComponentName";
import type {
  ComponentInvalidStatus,
  ComponentStoreData,
  ContentOptimizationErrors,
  ContentOptimizationErrorTextKey,
} from "./types";
import { contentOptimizationErrorTextKeys } from "./types";

export const validateContentOptimizationErrors = (
  componentStoreData: Partial<ComponentStoreData> | null,
  keyToMessageTexts: Partial<Record<ContentOptimizationErrorTextKey, string[]>> | null,
  options?: {
    maxVariantsAllowed?: number;
    skipValidation?: boolean;
    requireNonBodyActiveComponent?: boolean;
  }
): ContentOptimizationErrors => {
  // this skips validation if we're operating on a channel that is set up BUT does not have any CO
  // step logic built-in yet, since none of the CO settings will be populated anyway this prevents
  // validating against impossible state
  if (options?.skipValidation) {
    return {
      noComponentsConfigured: false,
      componentNamesWithRecursiveTags: [],
      componentNamesWithTooFewValues: [],
      componentNamesWithTooManyValues: [],
      componentNamesWithDuplicateValues: [],
      componentNamesWithWhitespaceOnlyValues: [],
      missingActiveComponentNames: [],
      keyToUnknownComponentNames: {},
      keyToInactiveComponentNames: {},
      isMessageIncomplete: false,
      overallComponentInvalidStatus: null,
      componentNameToInvalidStatus: {},
      opensOptimizationBodyOnly: false,
      clickOptimizationMissingLink: false,
      clickOptimizationAllLinksExcluded: false,
    };
  }

  const componentNameToValues = cleanComponentNameToValues(componentStoreData);
  const maxAllowed = options?.maxVariantsAllowed ?? maximumComponentValuesForActive;

  let noComponentsConfigured = true;
  const componentNamesWithTooFewValues = [] as string[];
  const componentNamesWithTooManyValues = [] as string[];
  const componentNamesWithRecursiveTags = [] as string[];
  const componentNamesWithDuplicateValues = [] as string[];
  const componentNamesWithWhitespaceOnlyValues = [] as string[];
  const componentNamesWithNoUsableValues = [] as string[];
  const activeComponentNames = [] as string[];
  for (const [componentName, values] of componentNameToValues) {
    const tooFewValues = values && values.length < minimumComponentValuesForActive;
    const tooManyValues = values && values.length > maxAllowed;
    if (tooFewValues) {
      componentNamesWithTooFewValues.push(componentName);
    } else if (tooManyValues) {
      componentNamesWithTooManyValues.push(componentName);
    } else {
      activeComponentNames.push(componentName);
    }

    const anyValueHasOurTag = values.some((value) => extractLiquidMessageComponentNames(value).size > 0);
    if (anyValueHasOurTag) {
      componentNamesWithRecursiveTags.push(componentName);
    }

    if (values.length > 0) {
      noComponentsConfigured = false;
    }
  }

  // Whitespace-only check runs on raw (uncleaned) values since filterComponentValues strips empties
  // but not whitespace-only strings like " "
  const rawComponents = componentStoreData?.components;
  const deactivatedValuesByComponent = componentStoreData?.deactivatedValues ?? {};
  if (rawComponents) {
    for (const [componentName, rawValues] of Object.entries(rawComponents)) {
      const values = (rawValues ?? []).filter((v): v is string => typeof v === "string");
      const nonWhitespaceValues = values.filter((value) => value.trim() !== "");
      if (new Set(nonWhitespaceValues).size < nonWhitespaceValues.length) {
        componentNamesWithDuplicateValues.push(componentName);
      }

      const deactivatedValues = new Set(deactivatedValuesByComponent[componentName] ?? []);
      const activeValues = values.filter((value) => !deactivatedValues.has(value));
      const hasWhitespaceOnly = values.some((value) => value.length > 0 && value.trim() === "");
      if (hasWhitespaceOnly) {
        componentNamesWithWhitespaceOnlyValues.push(componentName);
      }
      if (!activeValues.some((value) => value.trim() !== "")) {
        componentNamesWithNoUsableValues.push(componentName);
      }
    }
  }

  const keyToComponentNames = {} as Record<ContentOptimizationErrorTextKey, string[]>;
  const keyToUnknownComponentNames = {} as Record<ContentOptimizationErrorTextKey, string[]>;
  const keyToInactiveComponentNames = {} as Record<ContentOptimizationErrorTextKey, string[]>;
  const inactiveComponentNameSet = new Set([
    ...componentNamesWithTooFewValues,
    ...componentNamesWithNoUsableValues,
  ]);
  const allKeyComponentNames = new Set<string>();
  if (keyToMessageTexts) {
    for (const key of contentOptimizationErrorTextKeys) {
      const messageTexts = keyToMessageTexts[key];
      if (!messageTexts || messageTexts.length == 0) {
        continue;
      }
      const keyComponentNames = extractLiquidMessageComponentNames(...messageTexts);
      if (keyComponentNames.size > 0) {
        keyToComponentNames[key] = Array.from(keyComponentNames);
        const unknownComponentNames = [] as string[];
        const inactiveComponentNames = [] as string[];
        for (const keyComponentName of keyComponentNames) {
          // Canonicalize against store keys (case-insensitive).
          const canonicalComponentName = resolveComponentName(keyComponentName, componentNameToValues.keys());
          if (!canonicalComponentName) {
            unknownComponentNames.push(keyComponentName);
            allKeyComponentNames.add(keyComponentName);
          } else {
            allKeyComponentNames.add(canonicalComponentName);
            if (inactiveComponentNameSet.has(canonicalComponentName)) {
              inactiveComponentNames.push(canonicalComponentName);
            }
          }
        }
        if (unknownComponentNames.length > 0) {
          keyToUnknownComponentNames[key] = unknownComponentNames;
        }
        if (inactiveComponentNames.length > 0) {
          keyToInactiveComponentNames[key] = inactiveComponentNames;
        }
      }
    }
  }

  const missingActiveComponentNames = activeComponentNames.filter(
    (activeComponentName) => !allKeyComponentNames.has(activeComponentName)
  );

  // now that we're done with all the validation logic, let's calculate the computed data based off of it

  // these are based on the component screen, not the message screen, so only care about those errors
  const componentNameToInvalidStatus: Record<string, ComponentInvalidStatus> = {};
  let overallComponentInvalidStatus: ComponentInvalidStatus | null = null;
  // go from descending priority to higher for simplicity (we can just overwrite)
  for (const componentName of missingActiveComponentNames) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "warning";
  }
  for (const componentName of componentNamesWithTooFewValues) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "warning";
  }
  for (const componentName of componentNamesWithTooManyValues) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "warning";
  }
  if (noComponentsConfigured) {
    overallComponentInvalidStatus = "warning";
  }
  for (const componentName of componentNamesWithDuplicateValues) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "danger";
  }
  for (const componentName of componentNamesWithWhitespaceOnlyValues) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "danger";
  }
  for (const componentName of componentNamesWithRecursiveTags) {
    overallComponentInvalidStatus = componentNameToInvalidStatus[componentName] = "danger";
  }

  const opensOptimizationBodyOnly =
    (options?.requireNonBodyActiveComponent ?? false) &&
    activeComponentNames.length > 0 &&
    activeComponentNames.every(
      (componentName) => mapEmailMissingActiveComponentNameToComposerPanel(componentName) === "BODY"
    );
  if (opensOptimizationBodyOnly) {
    overallComponentInvalidStatus = "danger";
  }

  const hasUnknownComponentNames = Object.values(keyToUnknownComponentNames).some((names) => names.length > 0);
  const hasInactiveComponentNames = Object.values(keyToInactiveComponentNames).some((names) => names.length > 0);
  if (hasUnknownComponentNames || hasInactiveComponentNames) {
    overallComponentInvalidStatus = overallComponentInvalidStatus ?? "danger";
  }

  const isMessageIncomplete = noComponentsConfigured || missingActiveComponentNames.length > 0;

  return {
    componentNameToInvalidStatus,
    overallComponentInvalidStatus,

    noComponentsConfigured,
    componentNamesWithRecursiveTags,
    componentNamesWithTooFewValues,
    componentNamesWithTooManyValues,
    componentNamesWithDuplicateValues,
    componentNamesWithWhitespaceOnlyValues,

    missingActiveComponentNames,
    keyToUnknownComponentNames,
    keyToInactiveComponentNames,
    isMessageIncomplete,

    opensOptimizationBodyOnly,
    clickOptimizationMissingLink: false,
    clickOptimizationAllLinksExcluded: false,
  };
};
